import {
  CHARCOAL_5,
  OwnUpBody,
  OwnUpBodyMedium,
  PropsWithTheme
} from '@rategravity/own-up-component-library';
import { ErrorDarkIcon } from '@rategravity/own-up-component-library/icon-library/notification-icons/error-dark';
import { SuccessDarkIcon } from '@rategravity/own-up-component-library/icon-library/notification-icons/success-dark';
import { WarningDarkIcon } from '@rategravity/own-up-component-library/icon-library/notification-icons/warning-dark';
import React from 'react';
import styled from 'styled-components';
import { TitleWithIcon } from '../../../../title-with-icon';
import { maxStretchingDTI } from '../../constants';
import { Affordability } from '../../types';

export const Container = styled.div<{ backgroundColor?: string }>`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }: PropsWithTheme<{}>) => theme.spacing(3)}px;

  ${({ theme }: PropsWithTheme<{}>) => theme.breakpoints.up('md')} {
    row-gap: ${({ theme }: PropsWithTheme<{}>) => theme.spacing(1)}px;
  }

  filter: drop-shadow(4px 4px 8px ${CHARCOAL_5});
  padding: ${({ theme }: PropsWithTheme<{}>) => theme.spacing(4, 3)};

  border-radius: ${({ theme }: PropsWithTheme<{}>) => theme.spacing(0, 0, 1, 1)};
  background-color: ${({ backgroundColor }) => backgroundColor};

  flex: 1 1 auto;
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }: PropsWithTheme<{}>) => theme.spacing(3)}px;
`;

const AffordabilityDetailTitleIcon = React.memo(
  // eslint-disable-next-line react/prop-types
  ({ affordability }: { affordability: Affordability }) => {
    const icons = {
      Affordable: <SuccessDarkIcon />,
      Stretching: <WarningDarkIcon />,
      Aggressive: <ErrorDarkIcon />
    };

    return icons[affordability];
  }
);
AffordabilityDetailTitleIcon.displayName = 'AffordabilityDetailTitleIcon';

export const AffordabilityDetailTitle = React.memo(
  ({ affordability }: { affordability: Affordability }) => {
    return (
      <TitleWithIcon
        Icon={<AffordabilityDetailTitleIcon affordability={affordability} />}
        title={affordability}
      />
    );
  }
);
AffordabilityDetailTitle.displayName = 'AffordabilityDetailTitle';

const AffordabilityBody = React.memo(
  ({ downPaymentPercentage, dti }: { downPaymentPercentage: number; dti: number }) => (
    <React.Fragment>
      <OwnUpBody variant="body1" component="span">
        This scenario is{' '}
        <OwnUpBodyMedium variant="body1" component="span">
          affordable
        </OwnUpBodyMedium>
        .
      </OwnUpBody>
      <OwnUpBody variant="body1">
        Your current debt-to-income ratio is {Math.round(dti * 100)}%, which is likely to be
        approved by lenders. Your down payment is {Math.round(downPaymentPercentage * 100)}% of the
        total home price.
      </OwnUpBody>
    </React.Fragment>
  )
);
AffordabilityBody.displayName = 'AffordabilityBody';

const StretchingBody = React.memo(({ dti }: { dti: number }) => (
  <React.Fragment>
    <OwnUpBody variant="body1" component="span">
      This scenario{' '}
      <OwnUpBodyMedium variant="body1" component="span">
        may stretch your purchase power
      </OwnUpBodyMedium>
      .
    </OwnUpBody>
    <OwnUpBody variant="body1">
      Your current debt-to-income ratio is {Math.round(dti * 100)}%, pushing the limit of{' '}
      {Math.round(maxStretchingDTI * 100)}%.
    </OwnUpBody>
    <OwnUpBody variant="body1">
      To improve your chances of pre-approval, consider paying down debts, increasing your down
      payment, or building up your credit score.
    </OwnUpBody>
  </React.Fragment>
));
StretchingBody.displayName = 'StretchingBody';

const AggressiveBody = React.memo(({ dti }: { dti: number }) => (
  <React.Fragment>
    <OwnUpBody variant="body1" component="span">
      Lenders are{' '}
      <OwnUpBodyMedium variant="body1" component="span">
        unlikely to preapprove you
      </OwnUpBodyMedium>{' '}
      for this home price.
    </OwnUpBody>
    <OwnUpBody variant="body1">
      Your current debt-to-income ratio is {Math.round(dti * 100)}%, exceeding the limit of{' '}
      {Math.round(maxStretchingDTI * 100)}%.
    </OwnUpBody>
    <OwnUpBody variant="body1">
      To improve your chances of pre-approval, consider paying down debts, increasing your down
      payment, or building up your credit score.
    </OwnUpBody>
  </React.Fragment>
));
AggressiveBody.displayName = 'AggressiveBody';

export const BodyCopy = React.memo(
  ({
    dti,
    affordability,
    downPaymentPercentage
  }: {
    dti: number;
    affordability: Affordability;
    downPaymentPercentage: number;
  }) => {
    switch (affordability) {
      case 'Affordable':
        return <AffordabilityBody dti={dti} downPaymentPercentage={downPaymentPercentage} />;
      case 'Stretching':
        return <StretchingBody dti={dti} />;
      case 'Aggressive':
        return <AggressiveBody dti={dti} />;
    }
  }
);
BodyCopy.displayName = 'BodyCopy';
